import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import modalManager from '../modules/modalManager';
import promiseBreaker from '../modules/promiseBreaker';

import './Modal.scss';

const TRANSITION_TIMEOUT_MS = 500;

const KEY_TAB = 9;

/**
 * A general purpose modal container.
 *
 * @example
 *   <Modal
 *     title="Test Modal"
 *     initialOpen
 *     leftButton={Modal.CLOSE}
 *     rightButton={{
 *       ...Modal.SAVE,
 *       action: this.save,
 *     }}
 *   >
 *     <p>Hi! I am a modal!</p>
 *   </Modal>
 *
 * You can access the modal API with the ref attribute
 * @example
 *  modal = null;
 *  closeModal = () => {
 *    this.modal.close();
 *  }
 *  render() {
 *    return (
 *      <Modal
 *        ref={(m) => { this.modal = m; }}
 *        title="Test"
 *        initialOpen
 *      >
 *        <p>Hi! I am a modal!</p>
 *        <button onClick={this.closeModal}>
 *          Close me
 *        </button>
 *      </Modal>
 *    );
 *  }
 *
 */
export default class Modal extends React.PureComponent {
  static propTypes = {
    /** Open the modal as soon as it's mounted. */
    initialOpen: PropTypes.bool,

    /** The modal title */
    title: PropTypes.string,

    /** The left header button config. */
    leftButton: PropTypes.shape({
      className: PropTypes.string,
      action: PropTypes.func,
      text: PropTypes.string,
      label: PropTypes.string,
      testID: PropTypes.string,
    }),

    /** The right header button config. */
    rightButton: PropTypes.shape({
      className: PropTypes.string,
      action: PropTypes.func,
      enabled: PropTypes.bool,
      loading: PropTypes.bool,
      text: PropTypes.string,
      label: PropTypes.string,
      testID: PropTypes.string,
    }),

    /** Boolean to indicate if header should be hidden */
    hideHeader: PropTypes.bool,

    /** Boolean to indicate if the close X button should show in the body content of the modal */
    closeButtonInBody: PropTypes.bool,

    /** A class name to apply to the modal. */
    className: PropTypes.string,

    /** The CSS class for the open transition. */
    transitionOpen: PropTypes.string,

    /** The CSS class for the close transition. */
    transitionClose: PropTypes.string,

    /** The content of the modal. */
    children: PropTypes.any.isRequired,

    /** DO NOT SET THIS TO ANYTHING OTHER THAN WHAT'S WAS PASSED FROM `modalManager.openModal()`
     This is the element the modal was rendered into via modalManager.openModal() and is used to
     unmount & cleanup when the modal is closed. */
    renderContainer: PropTypes.object,

    /** Triggered when the modal is closed. */
    onClose: PropTypes.func,

    /** Triggered when the modal is opened. */
    onOpen: PropTypes.func,

    /** Any children to be placed in the footer, which will be fixed at the bottom of the modal */
    footerChildren: PropTypes.element,

    /** Specify class names for child components */
    classes: PropTypes.shape({
      modalComponent: PropTypes.string,
      modalContent: PropTypes.string,
      modalHeader: PropTypes.string,
      footer: PropTypes.string,
    }),

    zIndex: PropTypes.number,
  };

  /**  Button configs * */
  static CLOSE = {
    className: 'modal-close',
    label: 'Close',
    action: modal => modal.close(),
    testID: 'modal-close',
  };

  static CLOSE_TEXT = {
    className: 'modal-close-text',
    action: modal => modal.close(),
    text: 'Cancel',
    testID: 'modal-close',
  };

  static BACK = {
    className: 'modal-back',
    label: 'Back',
    action: modal => modal.close(),
    testID: 'modal-back',
  };

  static SAVE = {
    className: 'modal-save',
    text: 'Save',
    enabled: true,
    loading: false,
    action: modal => modal.close(),
    testID: 'modal-save',
  };

  /** Default properties * */
  static defaultProps = {
    initialOpen: false,
    title: null,
    leftButton: Modal.CLOSE,
    rightButton: null,
    hideHeader: false,
    closeButtonInBody: false,
    className: null,
    transitionOpen: 'slide-in-up',
    transitionClose: 'slide-out-down',
    renderContainer: null,
    onOpen: null,
    onClose: null,
    footerChildren: null,
    classes: {},
    zIndex: undefined,
  };

  state = {
    /** Modal is open and visible. */
    open: false,

    /** The Modal is open, but hidden. This is used when another modal is opened over it. */
    hidden: false,

    /** Transition CSS class. */
    transition: null,
  };

  containerRef = React.createRef();
  closeTransition = null;
  lastFocused = null;

  componentDidMount() {
    if (this.props.initialOpen) {
      this.open();
    }
    this.containerRef.current?.addEventListener?.('keydown', this.keyboardHandler);
  }

  componentDidUpdate(prevProps, prevState) {
    // If the modal just became visible, focus the header (for accessibility)
    if ((!prevState.open || prevState.hidden) && this.isVisible()) {
      this.focusModal();
    }
  }

  componentWillUnmount() {
    if (this.closeTransition) {
      this.closeTransition.cancel();
    }
    modalManager.modalClosed(this);
    this.containerRef.current?.removeEventListener?.('keydown', this.keyboardHandler);
  }

  /**
   * Remove the modal element from the container it was rendered into.
   */
  removeFromRenderContainer() {
    if (this.props.renderContainer) {
      modalManager.unmountModal(this.props.renderContainer);
    }
  }

  /**
   * Returns true if the current modal is open and not hidden
   */
  isVisible() {
    return this.state.open && !this.state.hidden;
  }

  /**
   * Open the modal
   *
   * @param {String} transitionClass - The CSS class transition to use.
   * @return {Promise} resolves when it's open.
   */
  open = transitionClass => {
    if (this.state.open) {
      return Promise.resolve();
    }

    // Resolve transition class
    // If transitionClass is an object, it might be an Event object passed from an onClick handler.
    let transition;
    if (transitionClass && typeof transitionClass === 'string') {
      transition = transitionClass;
    } else {
      transition = this.props.transitionOpen;
    }

    // Run transition and then change the state.
    const openPromise = new Promise(resolve => {
      if (transition) {
        this.setState({ transition, open: true }, () => {
          this.waitForTransition().then(resolve);
        });
      } else {
        this.setState({ open: true }, resolve);
      }
    });

    // Update modal manager
    openPromise.then(() => {
      modalManager.modalOpened(this);

      const { onOpen } = this.props;

      if (onOpen) {
        onOpen();
      }
    });

    return openPromise;
  };

  /**
   * Close the modal
   *
   * @param {String} transitionClass - The CSS class transition to use.
   * @return {Promise} resolves when it's closed.
   */
  close = transitionClass => {
    if (!this.state.open) {
      return Promise.resolve();
    }

    // Resolve transition class
    // If transitionClass is an object, it might be an Event object passed from an onClick handler.
    let transition;
    if (transitionClass && typeof transitionClass === 'string') {
      transition = transitionClass;
    } else {
      transition = this.props.transitionClose;
    }

    // Run transition and then change the state when it's complete.
    const closePromise = new Promise(resolve => {
      if (transition) {
        this.setState(() => ({ transition }), () => {
          this.closeTransition = promiseBreaker(this.waitForTransition(), { onCancel: resolve });
          this.closeTransition.then(() => {
            this.setState({ open: false, transition: null }, resolve);
          });
        });
      } else {
        this.setState({ open: false }, resolve);
      }
    });

    // Update the modal manager and unmount
    closePromise.then(() => {
      modalManager.modalClosed(this);
      this.removeFromRenderContainer();

      const { onClose } = this.props;

      if (onClose) {
        onClose();
      }
    });

    return closePromise;
  };

  /**
   * ONLY USED BY modalManager -- USE WITH CAUTION
   * Temporarily hide the modal.
   * This is used to keep the modal in the page, but hide it while another modal is open over it.
   */
  hide() {
    this.setState({ hidden: true, transition: null });
    this.lastFocused = document.activeElement;
  }

  /**
   * Unhide the modal
   * @return {Promise}
   */
  unhide() {
    return new Promise(resolve => {
      this.setState({ open: true, hidden: false, transition: null }, resolve);
    });
  }

  /**
   * Returns a promise that resolves when the transition on the container is complete.
   *
   * @return {Promise}
   */
  waitForTransition() {
    const containerEl = this.containerRef.current;
    if (!containerEl) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      let timeoutTimer;
      const done = () => {
        clearTimeout(timeoutTimer);
        containerEl.removeEventListener('animationend', done);
        containerEl.removeEventListener('transitionend', done);
        resolve();
      };
      containerEl.addEventListener('animationend', done);
      containerEl.addEventListener('transitionend', done);
      timeoutTimer = setTimeout(() => done(), TRANSITION_TIMEOUT_MS);
    });
  }

  /**
   * Return focus to the last element that was focused, or the header.
   * This is for both accessibility (https://bitsofco.de/accessible-modal-dialog/)
   * and if the modal was hidden, we should return the user to the field they were in.
   */
  focusModal() {
    // In rare cases, focusing elements can cause an exception.
    try {
      if (this.lastFocused) {
        this.lastFocused.focus();
      } else {
        const title = this.containerRef.current.querySelector('.modal-header .modal-title');
        if (title) {
          title.focus();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Handle global keyboard events.
   *
   * @param {Event} evt
   */
  keyboardHandler = evt => {
    if (!this.isVisible()) return;

    switch (evt.keyCode) {
      case KEY_TAB:
        this.trapTabbing(evt);
        break;
      default:
    }
  };

  /**
   * Keep tabbing to inside the modal.
   * For example, if the user is using the tab key to navigate through the form, when the get to
   * the last element, it should continue from the first focusable element in the modal.
   *
   * More info:
   * https://bitsofco.de/accessible-modal-dialog/
   *
   * @param {Event} evt
   */
  trapTabbing(evt) {
    const containerEl = this.containerRef.current;
    const focusableEls = containerEl.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]');
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    function handleBackwardTab() {
      if (document.activeElement === firstFocusableEl) {
        evt.preventDefault();
        lastFocusableEl.focus();
      }
    }
    function handleForwardTab() {
      if (document.activeElement === lastFocusableEl) {
        evt.preventDefault();
        firstFocusableEl.focus();
      }
    }

    if (evt.keyCode === KEY_TAB) {
      if (focusableEls.length === 1) {
        evt.preventDefault();
      } else if (evt.shiftKey) {
        handleBackwardTab();
      } else {
        handleForwardTab();
      }
    }
  }

  /**
   * Click handler for the left button
   */
  leftButtonHandler = () => {
    const { leftButton } = this.props;
    if (!leftButton || typeof leftButton.action !== 'function') {
      return;
    }
    leftButton.action(this);
  };

  /**
   * CLick handler for the right button
   */
  rightButtonHandler = () => {
    const { rightButton } = this.props;
    if (
      !rightButton
      || rightButton.loading
      || !rightButton.enabled
      || typeof rightButton.action !== 'function'
    ) {
      return;
    }
    rightButton.action(this);
  };

  render() {
    const {
      title,
      leftButton,
      rightButton,
      hideHeader,
      closeButtonInBody,
      className,
      children,
      classes,
      footerChildren,
    } = this.props;

    const {
      open,
      hidden,
      transition,
    } = this.state;

    return (
      <section
        className={classNames(
          'modal-component-wrapper',
          className,
          transition,
          { 'is-open': open && !hidden },
        )}
        role="dialog"
        aria-label={title}
        ref={this.containerRef}
        style={{ zIndex: this.props.zIndex }}
      >
        <div
          className="modal-mask"
          tabIndex="-1"
          onClick={this.close}
          role="presentation"
        />
        <div className={classNames('modal-component', classes.modalComponent)}>
          <header
            className={classNames(
              'modal-header',
              { 'hide-header': hideHeader },
              classes.modalHeader,
            )}
          >
            { leftButton && (
              <button
                className={classNames('left-button', leftButton.className)}
                onClick={this.leftButtonHandler}
                type="button"
                aria-label={leftButton.label}
                data-testid={leftButton.testID || 'modal__left-button'}
              >
                {leftButton.text}
              </button>
            )}
            { title && (
              <h1
                className="modal-title"
              >
                {title}
              </h1>
            )}
            { rightButton && (
              <button
                className={classNames(
                  'right-button',
                  rightButton.className,
                  {
                    'is-loading': rightButton.loading,
                    disabled: !rightButton.enabled,
                  },
                )}
                onClick={this.rightButtonHandler}
                disabled={!rightButton.enabled}
                aria-label={rightButton.label}
                type="button"
                data-testid={rightButton.testID || 'modal__right-button'}
              >
                {!rightButton.loading && rightButton.text}
              </button>
            )}
          </header>
          <div className={classNames(
            'modal-content',
            classes.modalContent,
            { 'no-padding': hideHeader },
          )}
          >
            {closeButtonInBody && (
              <span
                className="body-close-btn"
                onClick={this.close}
                role="presentation"
              />
            )}
            {open && children}
          </div>
          {footerChildren && (
            <footer className={classNames('modal-footer', classes.footer)}>
              {footerChildren}
            </footer>
          )}
        </div>
      </section>
    );
  }
}
